import { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  TextField,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  FormHelperText,
  Box,
  Typography,
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import api from '../../config/api';
import { useTranslation } from 'react-i18next';
import useStore from '../../config/store';
import { getStatusLabel, isVisibleInPortal } from './positionStatus';

/**
 * Diálogo para incluir postulantes (applicants legacy) existentes del proyecto
 * en una vacante ATS. Solo se monta para quien puede editar la vacante.
 *
 * Props:
 * - open, onClose
 * - positionId: vacante destino
 * - projectId: proyecto de la vacante (para listar applicants)
 * - position: la vacante (estado, "Visible en portal" y tipo, para saber si se
 *   puede enviar la invitación)
 * - onAdded: () => void  (refrescar la lista de candidatos)
 */
const AtsAddApplicantsDialog = ({ open, onClose, positionId, projectId, position = null, onAdded }) => {
  const { t, i18n } = useTranslation();

  const [applicants, setApplicants] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [search, setSearch] = useState('');
  const [sendInvitation, setSendInvitation] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Masivas con límite: ¿ya lo alcanzó? (el portal ya no la muestra)
  const withLimit = position?.tipo === 'massive' && Number(position?.captacion?.limit) > 0;
  const [limitReached, setLimitReached] = useState(false);
  const [checkingLimit, setCheckingLimit] = useState(false);
  useEffect(() => {
    setLimitReached(false);
    setCheckingLimit(false);
    if (!open || !withLimit || !positionId) return undefined;
    let alive = true;
    const { authToken } = useStore.getState();
    setCheckingLimit(true);
    api.get(`/captation/${positionId}/status`, { headers: { Authorization: `Bearer ${authToken}` } })
      .then(({ data }) => {
        if (alive && data.response?.status_response === 'OK') setLimitReached(Boolean(data.response.data?.limitReached));
      })
      // Si falla la consulta no se bloquea: el backend revisa lo mismo al enviar
      .catch(() => {})
      .finally(() => { if (alive) setCheckingLimit(false); });
    return () => { alive = false; };
  }, [open, withLimit, positionId]);

  // La invitación lleva al portal de la vacante: solo se puede enviar si la
  // vacante recibe postulaciones ahí (misma regla y orden que el backend,
  // getPortalClosedReason; 2026-10-08)
  const invitationBlock = !position ? null
    : position.status !== 'active' ? 'not_active'
      : !isVisibleInPortal(position) ? 'not_visible'
        : limitReached ? 'limit_reached'
          : null;
  const invitationDisabled = Boolean(invitationBlock) || checkingLimit;

  useEffect(() => {
    if (!open || !projectId) return;

    const loadApplicants = async () => {
      const { authToken, showAlert, organization } = useStore.getState();
      try {
        setLoading(true);
        setSelectedIds([]);
        setSearch('');
        setSendInvitation(false);

        // organizationId (2026-09-07): incluye el banco de talento de la org
        // (postulantes sin proyecto) para poder sumarlos a la vacante
        const response = await api.get(`/applicants`, {
          params: { projectId, organizationId: organization || undefined },
          headers: { Authorization: `Bearer ${authToken}` },
        });

        if (response.data.response?.status_response === 'OK') {
          setApplicants(response.data.response.data || []);
        } else {
          showAlert(t('ats.detail.addApplicants.loadError'), 'warning');
        }
      } catch (err) {
        console.error(err);
        showAlert(t('ats.detail.addApplicants.loadError'), 'warning');
      } finally {
        setLoading(false);
      }
    };

    loadApplicants();
  }, [open, projectId, t]);

  const filtered = useMemo(() => {
    if (!search.trim()) return applicants;
    const term = search.toLowerCase();
    return applicants.filter(
      (a) =>
        a.name?.toLowerCase().includes(term) ||
        a.email?.toLowerCase().includes(term)
    );
  }, [applicants, search]);

  const toggle = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleAdd = async () => {
    const { authToken, showAlert } = useStore.getState();
    if (selectedIds.length === 0) return;

    try {
      setSaving(true);
      const response = await api.post(
        `/applications/from-applicants`,
        {
          positionId,
          applicantIds: selectedIds,
          sendInvitation: sendInvitation && !invitationDisabled,
          language: i18n.language === 'en' ? 'en' : 'es',
        },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );

      const data = response.data.response?.data;
      if (response.data.response?.status_response === 'OK' && data?.summary) {
        const s = data.summary;
        const summaryText = t('ats.detail.addApplicants.summary', {
          added: s.added,
          already: s.alreadyExists,
          missing: s.missingCv,
        });
        // Avisos (PLAN_IMPORTACION_COMPUTRABAJO, fase 0): cuenta del portal con
        // otro RUT (se agregó sin vincularla) y fichas sin correo (se agregan
        // "Por validar")
        const results = Array.isArray(data.results) ? data.results : [];
        const namesOf = (rows) => rows.map((r) => r.name || r.email || r.applicantId).join(', ');
        const rutMismatch = results.filter((r) => r.warning === 'ACCOUNT_RUT_MISMATCH');
        const pendingValidation = results.filter((r) => r.pendingValidation);
        const warnings = [
          ...(rutMismatch.length > 0 ? [t('ats.detail.addApplicants.rutMismatch', { names: namesOf(rutMismatch) })] : []),
          ...(pendingValidation.length > 0 ? [t('ats.detail.addApplicants.pendingValidation', { names: namesOf(pendingValidation) })] : []),
          // La vacante dejó de recibir postulaciones en el portal mientras el
          // diálogo estaba abierto: el backend no envió las invitaciones
          ...(s.invitationsBlocked ? [t('ats.portalInvitation.notSent')] : []),
        ];
        showAlert(
          warnings.length > 0 ? [summaryText, ...warnings].join(' · ') : summaryText,
          warnings.length > 0 ? 'warning' : 'success'
        );
        if (onAdded) onAdded();
        onClose();
      } else {
        showAlert(t('ats.detail.addApplicants.saveError'), 'error');
      }
    } catch (err) {
      console.error(err);
      showAlert(t('ats.detail.addApplicants.saveError'), 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('ats.detail.addApplicants.title')}</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          {t('ats.detail.addApplicants.description')}
        </DialogContentText>

        <TextField
          size="small"
          fullWidth
          placeholder={t('ats.detail.addApplicants.search')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ mb: 1 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : filtered.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
            {t('ats.detail.addApplicants.noApplicants')}
          </Typography>
        ) : (
          <List dense sx={{ maxHeight: 320, overflow: 'auto', border: '1px solid #E5E7EB', borderRadius: 1 }}>
            {filtered.map((a) => {
              const id = a.id || a.uid;
              const hasCv = !!a.cvUrl;
              return (
                <ListItem key={id} disablePadding
                  secondaryAction={
                    <Chip
                      label={hasCv ? t('ats.detail.addApplicants.withCv') : t('ats.detail.addApplicants.withoutCv')}
                      size="small"
                      color={hasCv ? 'success' : 'default'}
                      variant={hasCv ? 'filled' : 'outlined'}
                    />
                  }
                >
                  <ListItemButton onClick={() => toggle(id)} dense>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <Checkbox edge="start" checked={selectedIds.includes(id)} tabIndex={-1} disableRipple />
                    </ListItemIcon>
                    <ListItemText
                      primary={a.name || a.email}
                      secondary={a.email || t('ats.detail.addApplicants.noEmailSecondary')}
                    />
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>
        )}

        <FormControlLabel
          sx={{ mt: 1 }}
          disabled={invitationDisabled}
          control={
            <Checkbox
              checked={sendInvitation && !invitationDisabled}
              onChange={(e) => setSendInvitation(e.target.checked)}
            />
          }
          label={t('ats.detail.addApplicants.sendInvitation')}
        />
        {invitationBlock && (
          <FormHelperText sx={{ mt: 0, ml: 4 }}>
            {t(`ats.portalInvitation.blocked.${invitationBlock}`, { status: getStatusLabel(position.status, t) })}
          </FormHelperText>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          {t('ats.detail.addApplicants.cancel')}
        </Button>
        <Button
          onClick={handleAdd}
          variant="contained"
          disabled={saving || loading || selectedIds.length === 0}
          sx={{ backgroundColor: '#6A5AF9', '&:hover': { backgroundColor: '#5B4DE8' } }}
        >
          {saving ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            `${t('ats.detail.addApplicants.add')}${selectedIds.length ? ` (${selectedIds.length})` : ''}`
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AtsAddApplicantsDialog;
